import { QueryClient, keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { temaService } from '../services/temaService';
import { FiltrosCatalogo, Pagina, Tema, TemaApp, TemaInput } from '../types/tema';

/** Catálogo público (`/temas`): filtros y tanda los resuelve el backend. */
export function useTemas(filtros: FiltrosCatalogo = {}) {
  return useQuery({
    queryKey: ['temas', 'publicos', filtros],
    queryFn: () => temaService.list(filtros),
    placeholderData: keepPreviousData,
  });
}

/** Catálogo de la app autenticada: filtros y paginación los resuelve el backend.
 *  `keepPreviousData` evita el parpadeo al cambiar de página o de filtro. */
export function useTemasApp(filtros: FiltrosCatalogo = {}) {
  return useQuery({
    queryKey: ['temas', 'app', filtros],
    queryFn: () => temaService.listApp(filtros),
    placeholderData: keepPreviousData,
  });
}

/**
 * Cómo se apilan las tandas de un catálogo con scroll infinito.
 *
 * `gcTime` largo a propósito: al volver de la ficha de un tema el catálogo se
 * monta de nuevo y las tandas se recuperan de la caché de una vez, así la fila de
 * la que se viene sigue en la lista para destacarla. `staleTime` evita que esa
 * vuelta dispare de nuevo la consulta de cada tanda cargada.
 */
const TANDAS = {
  // `number` explícito: si se infiere el literal `1`, `pageParam` deja de aceptar
  // las páginas siguientes.
  initialPageParam: 1 as number,
  staleTime: 60 * 1000,
  gcTime: 30 * 60 * 1000,
  placeholderData: keepPreviousData,
};

/** Catálogo público por tandas (scroll infinito). */
export function useTemasInfinito(filtros: FiltrosCatalogo = {}) {
  return useInfiniteQuery({
    ...TANDAS,
    queryKey: ['temas', 'publicos', 'tandas', filtros],
    queryFn: ({ pageParam }) => temaService.list({ ...filtros, page: pageParam }),
    getNextPageParam: (ultima: Pagina<Tema>, cargadas) => (ultima.next ? cargadas.length + 1 : undefined),
  });
}

/** Catálogo de la app por tandas (scroll infinito). */
export function useTemasAppInfinito(filtros: FiltrosCatalogo = {}) {
  return useInfiniteQuery({
    ...TANDAS,
    queryKey: ['temas', 'app', 'tandas', filtros],
    queryFn: ({ pageParam }) => temaService.listApp({ ...filtros, page: pageParam }),
    getNextPageParam: (ultima: Pagina<TemaApp>, cargadas) => (ultima.next ? cargadas.length + 1 : undefined),
  });
}

/** Profesores que ofrecen temas, para el autocompletado del filtro "Guía". */
export function useGuiasTemas(q: string) {
  return useQuery({
    queryKey: ['temas', 'guias', q],
    queryFn: () => temaService.guias(q),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
}

/** Ficha de un tema (página compartible). */
export function useTema(id: number) {
  return useQuery({ queryKey: ['temas', id], queryFn: () => temaService.get(id), enabled: Number.isFinite(id) });
}

/** Alta de un tema; al crear invalida el catálogo (['temas'] cubre ['temas','app']). */
export function useCreateTema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TemaInput) => temaService.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['temas'] }),
  });
}

/** Edita un tema; invalida el catálogo y la ficha. */
export function useUpdateTema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: TemaInput }) => temaService.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['temas'] }),
  });
}

/** Pausa un tema o lo devuelve al catálogo; invalida el catálogo y la ficha. */
/** El coordinador le da cuenta al coguía externo: deja de ser texto y pasa a ser persona. */
export function useDarAccesoCoguia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => temaService.darAccesoCoguia(id),
    // Cambia el coguía del tema y baja a los procesos abiertos, así que se invalidan
    // los tres: la ficha del tema, los procesos donde ahora aparece, y el aviso de
    // la barra, que acaba de tener un pendiente menos.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['temas'] });
      qc.invalidateQueries({ queryKey: ['instancias'] });
      qc.invalidateQueries({ queryKey: ['externos-pendientes'] });
    },
  });
}

export function usePausarTema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, pausar }: { id: number; pausar: boolean }) => temaService.pausar(id, pausar),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['temas'] }),
  });
}

/**
 * Cambia `es_favorito` en todo lo que ya está en caché, en vez de invalidar.
 *
 * Invalidar `['temas']` volvería a pedir **todas** las tandas cargadas del
 * catálogo —y a atenuar la lista entera— cada vez que alguien toca una estrella,
 * que es justo lo que se hace muchas veces seguidas mientras se recorre.
 *
 * Toca solo el catálogo de la app y la ficha: el catálogo público no lleva esta
 * marca, y `['temas','guias'|'opciones']` guardan otra cosa.
 */
function parchearFavorito(qc: QueryClient, id: number, favorito: boolean) {
  const parchear = (t: TemaApp) => (t?.id === id ? { ...t, es_favorito: favorito } : t);
  qc.setQueriesData({ queryKey: ['temas', 'app'] }, (datos: any) => {
    if (!datos) return datos;
    if (datos.pages) return { ...datos, pages: datos.pages.map((p: Pagina<TemaApp>) => ({ ...p, results: p.results.map(parchear) })) };
    if (datos.results) return { ...datos, results: datos.results.map(parchear) };
    return datos;
  });
  qc.setQueryData(['temas', id], (tema: any) => (tema ? { ...tema, es_favorito: favorito } : tema));
}

/**
 * Marca como vieja la lista filtrada por favoritos, que es la única cuyo
 * **contenido** cambia al tocar una estrella: en las demás basta con el parche.
 *
 * Parchear no alcanza ahí porque quién entra en esa lista lo decide el servidor,
 * y el parche solo cambia la marca de los temas que ya están en la caché. Sin
 * esto, prender el filtro dentro del minuto siguiente (el `staleTime`) devolvía
 * la lista guardada, sin el tema recién marcado y sin pedir nada.
 *
 * Si esa lista no está a la vista, invalidar no pide nada ahora: solo la deja
 * vieja para que se recargue cuando se prenda el filtro. Ese es el caso normal
 * mientras uno recorre el catálogo marcando temas.
 */
function invalidarListaDeFavoritos(qc: QueryClient) {
  qc.invalidateQueries({
    predicate: (query) => {
      const [raiz, ambito] = query.queryKey as [string, string?];
      const filtros = query.queryKey[query.queryKey.length - 1] as { favoritos?: number } | undefined;
      return raiz === 'temas' && ambito === 'app' && !!filtros && typeof filtros === 'object' && filtros.favoritos === 1;
    },
  });
}

/**
 * La estrella de un tema. Optimista: responde al toque y se devuelve sola si el
 * servidor la rechaza.
 */
export function useMarcarFavorito() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, favorito }: { id: number; favorito: boolean }) => temaService.favorito(id, favorito),
    onMutate: ({ id, favorito }) => parchearFavorito(qc, id, favorito),
    onError: (_error, { id, favorito }) => parchearFavorito(qc, id, !favorito),
    onSettled: () => invalidarListaDeFavoritos(qc),
  });
}

/** Elimina un tema; invalida el catálogo. */
export function useDeleteTema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => temaService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['temas'] }),
  });
}

/** Áreas de investigación para el select de categoría (crear tema). */
export function useAreas() {
  return useQuery({ queryKey: ['areas'], queryFn: temaService.areas, staleTime: 5 * 60 * 1000 });
}

/** Crear / borrar áreas (coordinador): refresca las áreas y los temas (muestran áreas). */
function useInvalidarAreas() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['areas'] });
    qc.invalidateQueries({ queryKey: ['temas'] });
  };
}

export function useCrearArea() {
  const invalidar = useInvalidarAreas();
  return useMutation({ mutationFn: (nombre: string) => temaService.crearArea(nombre), onSuccess: invalidar });
}

/** Cambia la supercategoría de un área; refresca el catálogo y la vista de docentes. */
export function useAgruparArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, supercategoria }: { id: number; supercategoria: number | null }) =>
      temaService.agruparArea(id, supercategoria),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['areas'] });
      qc.invalidateQueries({ queryKey: ['superareas'] });
      qc.invalidateQueries({ queryKey: ['docentes-publicos'] });
    },
  });
}

export function useBorrarArea() {
  const invalidar = useInvalidarAreas();
  return useMutation({ mutationFn: (id: number) => temaService.borrarArea(id), onSuccess: invalidar });
}

/** Docentes candidatos a coguía interno; se refiltra por `q` en el backend. */
export function useDocentes(q: string, enabled: boolean, incluirYo = false) {
  return useQuery({
    queryKey: ['docentes', q, incluirYo],
    queryFn: () => temaService.docentes(q, incluirYo),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Temas como opciones para reasignar (coordinador); búsqueda por título. */
export function useTemasOpciones(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['temas', 'opciones', q],
    queryFn: () => temaService.opciones(q),
    enabled,
    placeholderData: keepPreviousData,
  });
}
